import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { Envelope, MagnifyingGlass, PaperPlaneTilt, Star, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function previousMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export default function AdminDigest() {
  const [clients, setClients] = useState([]);
  const init = previousMonth();
  const [year, setYear] = useState(init.year);
  const [month, setMonth] = useState(init.month);
  const [selectedClient, setSelectedClient] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingOne, setSendingOne] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [forceBroadcast, setForceBroadcast] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, []);

  const fetchPreview = async () => {
    if (!selectedClient) return toast.error("Pick a client first");
    setLoadingPreview(true);
    setPreview(null);
    try {
      const { data } = await api.get(`/admin/digest/preview/${selectedClient}?year=${year}&month=${month}`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendOne = async () => {
    if (!selectedClient) return toast.error("Pick a client first");
    if (!window.confirm(`Send the ${MONTH_NAMES[month - 1]} ${year} digest to this client?`)) return;
    setSendingOne(true);
    try {
      const { data } = await api.post(`/admin/digest/send/${selectedClient}?year=${year}&month=${month}`);
      toast.success(`Sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSendingOne(false);
    }
  };

  const sendAll = async () => {
    const note = forceBroadcast ? " (force = resend even if already sent)" : "";
    if (!window.confirm(`Broadcast the ${MONTH_NAMES[month - 1]} ${year} digest to ALL eligible clients?${note}`)) return;
    setBroadcasting(true);
    setLastResult(null);
    try {
      const { data } = await api.post(`/admin/digest/send-all?year=${year}&month=${month}&force=${forceBroadcast}`);
      setLastResult(data);
      toast.success(`Sent ${data.sent} digests`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Engagement</div>
        <h1 className="font-display text-5xl mt-2">Monthly digest</h1>
        <p className="text-sm text-[#a3a3a3] mt-3 max-w-2xl">
          Auto-fires for each active client on the 1st of every month (covering the previous
          month). Use this page to preview the email, manually fire it for a specific client, or
          replay a missed month.
        </p>
      </div>

      {/* Period selector */}
      <div className="border border-[#262626] bg-[#0a0a0a] p-5 mb-6" data-testid="digest-period">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-3">Period</div>
        <div className="flex gap-3 flex-wrap">
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            data-testid="digest-month"
            className="bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
          >
            {MONTH_NAMES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="digest-year"
            className="bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
          >
            {Array.from({ length: 4 }, (_, i) => init.year - 2 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Single-client preview & send */}
      <div className="border border-[#262626] bg-[#0a0a0a] p-5 mb-6" data-testid="digest-single">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D] mb-3">Preview · Single client</div>
        <div className="flex gap-3 flex-wrap items-stretch">
          <select
            value={selectedClient}
            onChange={(e) => { setSelectedClient(e.target.value); setPreview(null); }}
            data-testid="digest-client"
            className="flex-1 min-w-[260px] bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
          >
            <option value="">— Select a client —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.company_name ? `(${c.company_name})` : ""} · {c.email}
              </option>
            ))}
          </select>
          <button
            onClick={fetchPreview}
            disabled={loadingPreview || !selectedClient}
            data-testid="digest-preview-btn"
            className="btn-outline px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
          >
            <MagnifyingGlass size={12} />
            {loadingPreview ? "Loading…" : "Preview"}
          </button>
          <button
            onClick={sendOne}
            disabled={sendingOne || !selectedClient}
            data-testid="digest-send-one-btn"
            className="btn-primary px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
          >
            <PaperPlaneTilt size={12} weight="fill" />
            {sendingOne ? "Sending…" : "Send to client"}
          </button>
        </div>

        {preview && (
          <div className="mt-5 border border-[#262626] bg-[#050505] p-5" data-testid="digest-preview">
            {preview.empty ? (
              <div className="text-sm text-[#a3a3a3]">
                <Star size={14} className="inline text-[#a3a3a3] mr-1" />
                {preview.message}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div>
                    <div className="font-display text-2xl">{preview.client.name}</div>
                    <div className="text-xs text-[#a3a3a3]">{preview.client.email}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">
                    {preview.period.label}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-[#262626] border border-[#262626] mb-4">
                  <Stat label="Delivered" value={preview.delivered_count} />
                  <Stat label="New briefs" value={preview.new_projects_count} />
                  <Stat label="Invested" value={formatUSD(preview.spend_usd)} small />
                  <Stat label="Reviews" value={preview.reviews_count} />
                  <Stat label="Referral pts" value={`+${preview.referral_points_earned}`} />
                </div>
                {preview.delivered_projects?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-2">Delivered this month</div>
                    <ul className="divide-y divide-[#262626] border border-[#262626]">
                      {preview.delivered_projects.map((p) => (
                        <li key={p.id} className="px-3 py-2 text-sm flex justify-between gap-3">
                          <span className="truncate">{p.title}</span>
                          <span className="text-[#a3a3a3] capitalize text-xs shrink-0">{(p.service_type || "").replace(/_/g, " ")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Broadcast */}
      <div className="border border-[#FF2D2D]/40 bg-gradient-to-r from-[#FF2D2D]/10 to-transparent p-5" data-testid="digest-broadcast">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Broadcast</div>
            <div className="font-display text-xl mt-1">Send {MONTH_NAMES[month - 1]} {year} digest to all clients</div>
            <p className="text-xs text-[#a3a3a3] mt-2 max-w-xl">
              Skips: opted-out clients, clients already sent for this period (unless <em>force</em>),
              and clients with zero activity that month.
            </p>
            <label className="flex items-center gap-2 text-xs mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={forceBroadcast}
                onChange={(e) => setForceBroadcast(e.target.checked)}
                data-testid="digest-force"
                className="accent-[#FF2D2D]"
              />
              Force resend (override already-sent guard)
            </label>
          </div>
          <button
            onClick={sendAll}
            disabled={broadcasting}
            data-testid="digest-broadcast-btn"
            className="btn-primary px-5 py-3 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
          >
            <Envelope size={14} weight="fill" />
            {broadcasting ? "Broadcasting…" : "Broadcast now"}
          </button>
        </div>
        {lastResult && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-px bg-[#262626] border border-[#262626]" data-testid="digest-result">
            <Stat label="Sent" value={lastResult.sent} accent />
            <Stat label="Opted out" value={lastResult.skipped_opted_out} />
            <Stat label="Already sent" value={lastResult.skipped_already_sent} />
            <Stat label="No activity" value={lastResult.skipped_no_activity} />
            <Stat label="Errors" value={lastResult.errors} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, accent, small }) {
  return (
    <div className="bg-[#050505] p-3 text-center">
      <div className={`font-display tabular-nums ${small ? "text-base" : "text-2xl"} ${accent ? "text-[#FF2D2D]" : ""}`}>
        {value}
      </div>
      <div className="text-[9px] uppercase tracking-wider text-[#a3a3a3] mt-1">{label}</div>
    </div>
  );
}
